/**
 * SocialProofToast — rotating bottom-left toast with real recent activity.
 *
 * Pulls anonymized activity from /api/social-proof/recent (quote requests,
 * reviews, completed projects) and cycles through them every ~8s. Silent if
 * feed is empty. Dismissible for the session.
 *
 * Design: dark glass card, cyan neon accent, small enough to not distract
 * but visible enough to trigger social-proof heuristics ("others are
 * hiring them right now").
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Bell, Star, Sparkles, Building, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const DISMISS_KEY = "sm_social_dismissed";
const START_DELAY_MS = 8000;
const ROTATE_MS = 8000;

const ICONS = {
  quote: Sparkles,
  review: Star,
  project: Building,
};

const LABELS = {
  quote: (i) => `${i.first_name || "Klient"}${i.city ? ` z ${i.city}` : ""} si vyžiadal cenovú ponuku`,
  review: (i) => `${i.first_name || "Klient"}${i.city ? ` z ${i.city}` : ""} napísal ${i.rating || 5}⭐ recenziu`,
  project: (i) => `Nový projekt: ${i.title || "Stavba"}${i.city ? ` — ${i.city}` : ""}`,
};

export default function SocialProofToast() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(DISMISS_KEY)) setDismissed(true);
    } catch {
      /* ignore */
    }
    axios
      .get(`${API}/social-proof/recent?limit=10`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, []);

  // Show first toast after delay; rotate every ROTATE_MS.
  useEffect(() => {
    if (dismissed || items.length === 0) return;
    const startT = setTimeout(() => setVisible(true), START_DELAY_MS);
    const rotateT = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % items.length);
        setVisible(true);
      }, 500);
    }, ROTATE_MS);
    return () => {
      clearTimeout(startT);
      clearInterval(rotateT);
    };
  }, [items.length, dismissed]);

  const close = () => {
    setVisible(false);
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  if (dismissed || !items.length) return null;
  const item = items[idx];
  if (!item) return null;
  const Icon = ICONS[item.kind] || Bell;
  const label = LABELS[item.kind]?.(item) || "Nová aktivita";

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key={idx}
          initial={{ opacity: 0, x: -30, y: 20 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: -30, y: 20 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-4 left-4 z-40 max-w-xs sm:max-w-sm pointer-events-auto"
          data-testid="social-proof-toast"
        >
          <div className="flex items-start gap-3 pl-3 pr-8 py-3 bg-black/85 backdrop-blur-md border border-[#00E5FF]/25 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.6),0_0_20px_rgba(0,229,255,0.15)] relative">
            <div className="w-8 h-8 rounded-full bg-[#00E5FF]/15 border border-[#00E5FF]/40 flex items-center justify-center flex-shrink-0">
              <Icon size={14} className="text-[#00E5FF]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-[#00E5FF]/80 font-bold">
                {item.kind === "quote"
                  ? "Nová ponuka"
                  : item.kind === "review"
                  ? "Nová recenzia"
                  : "Nový projekt"}
              </div>
              <div className="text-sm text-white mt-0.5 leading-snug">{label}</div>
              {item.text && (
                <div className="text-[11px] text-zinc-400 italic mt-1 line-clamp-2">
                  "{item.text}"
                </div>
              )}
              <div className="text-[10px] text-zinc-500 mt-1">{item.when}</div>
            </div>
            <button
              onClick={close}
              aria-label="Zavrieť"
              className="absolute top-2 right-2 text-zinc-500 hover:text-white w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/5 transition-colors"
              data-testid="social-proof-close"
            >
              <X size={12} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
